import React, { useCallback, useMemo, useState } from 'react'
import { colors, IconButton, Box, AppBar, AppBarProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Close, Menu } from '@mui/icons-material'
import NavigationButton from '@talentinc/gatsby-theme-ecom/components/Buttons/NavigationButton'
import Link from '@talentinc/gatsby-theme-ecom/components/Link'
import { ContentfulModalStep } from '@talentinc/gatsby-theme-ecom/components/Modals/ContentfulModal'
import NavigationGroup, {
  NavigationGroupProps,
} from '@talentinc/gatsby-theme-ecom/components/NavigationGroup/'
import ScrollProgress from '@talentinc/gatsby-theme-ecom/components/Post/ScrollProgress'
import { withBannerAccommodator } from '@talentinc/gatsby-theme-ecom/hocs/withBannerAccommodator'
import useBrand from '@talentinc/gatsby-theme-ecom/hooks/useBrand'
import usePageData from '@talentinc/gatsby-theme-ecom/hooks/usePageData'
import usePageMeasurements from '@talentinc/gatsby-theme-ecom/hooks/usePageMeasurements'
import { useBETelemetry } from '../../hooks/useTelemetry'

import {
  Modal,
  ModalStep,
  ModalVariants,
} from '@talentinc/gatsby-theme-ecom/types/modal'
import {
  ActionLink,
  Button,
  ButtonRole,
} from '@talentinc/gatsby-theme-ecom/types/page'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import useLocation from 'react-use/lib/useLocation'
import useLockBodyScroll from 'react-use/lib/useLockBodyScroll'
import useUser from '../../hooks/useUser'
import { ContentfulIds } from '../../types/contentfulAttributes'
import BlogNavigation from './BlogNavigation'

enum MenuState {
  MENU_CLOSED = 0,
  MENU_OPENED = 1,
  REGIONS_OPENED = 2,
}

type Props = {
  showBlogNavigation?: boolean
  noNavigation?: boolean
  position?: AppBarProps['position']
}

const HeaderV2: React.FC<Props> = ({
  showBlogNavigation,
  noNavigation,
  position = 'sticky',
}) => {
  const { classes, cx } = useStyles()
  const brand = useBrand()
  const { partnerCoBrand } = useUser()
  const { t } = useTranslation()
  const { headerV2 } = usePageData()
  const telemetry = useBETelemetry() // Initialize telemetry hook

  const regionsSelectStep: ModalStep = useMemo(() => {
    const languageSwitcherBtn: Button<ActionLink> =
      headerV2?.navigationRightSide.find(
        (v) =>
          v.__typename === 'ContentfulButton' &&
          v.role === ButtonRole.LanguageSwitcher
      ) as Button<ActionLink>
    const modal = languageSwitcherBtn?.action as Modal
    return (modal?.steps || [])[0] as ModalStep
  }, [headerV2?.navigationRightSide])

  const buyNowButton = useMemo(
    () =>
      headerV2?.navigationRightSide.find(
        (v) =>
          v.__typename === 'ContentfulButton' &&
          v.role !== ButtonRole.LanguageSwitcher
      ),
    [headerV2?.navigationRightSide]
  )
  const [menuState, setMenuState] = useState(MenuState.MENU_CLOSED)
  const open = menuState !== MenuState.MENU_CLOSED
  const [expanded, setExpanded] = useState<string | false>(false)

  const {
    header: [headerRef],
  } = usePageMeasurements()

  // Lock the body scroll if the mobile nav is open.
  useLockBodyScroll(open)

  const { pathname = '' } = useLocation()
  const pathNameWithoutLeadingSlash = pathname.slice(1)

  // HIW - How It Works
  const shouldReplaceWithHIW = useCallback(
    (contentfulId: string) =>
      [
        t('slugs.pricing', { product: brand.flagshipProduct?.toLowerCase() }),
        t('slugs.newpricing', {
          product: brand.flagshipProduct?.toLowerCase(),
        }),
        t('slugs.newpricingfour', {
          product: brand.flagshipProduct?.toLowerCase(),
        }),
        t('slugs.jobsearch', {
          product: brand.flagshipProduct?.toLowerCase(),
        }),
        t('slugs.jobsearchlow', {
          product: brand.flagshipProduct?.toLowerCase(),
        }),
        t('slugs.jobsearchhigh', {
          product: brand.flagshipProduct?.toLowerCase(),
        }),
      ].some((slug) => pathname?.startsWith(slug)) &&
      [
        ContentfulIds.ResumeServicesNavGroup,
        ContentfulIds.CVServicesNavGroup,
      ].some((id) => id === contentfulId),
    [pathname, t, brand.flagshipProduct]
  )

  if (!headerV2 || !brand) {
    return null
  }

  const handleClickChange =
    (panel: string) =>
    (_: React.ChangeEvent<unknown>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false)
    }

  const showRegions = () => {
    setMenuState(MenuState.REGIONS_OPENED)
  }

  const renderButton = (button: Button<ActionLink>) => {
    if (button.role === ButtonRole.LanguageSwitcher) {
      return (
        <NavigationButton
          link={button}
          className={cx(classes.langBtn, classes.navButton)}
        />
      )
    }
    return !headerV2.hideBuyNowButton?.includes(pathNameWithoutLeadingSlash) ? (
      <NavigationButton
        link={button}
        className={classes.navButton}
        onClick={() => {
          telemetry.track({ event: 'click_buy_now_header' }) // Track the "Buy Now" event
        }}
      />
    ) : null
  }

  return (
    <AppBar
      id="default-rheader"
      className={cx(classes.appBar, {
        [classes.appBarWihoutNavigation]: noNavigation,
      })}
      component="header"
      elevation={0}
      position={position}
      color="inherit"
      ref={headerRef}
      data-gtm-role="header"
    >
      <Box className={classes.topBar}>
        <Link href="/" className={classes.logoLink}>
          <img
            className={classes.logo}
            alt={partnerCoBrand?.name ?? brand.name}
            src={partnerCoBrand?.logo ?? brand.logo?.file.url}
            width="100%"
            height="100%"
          />
        </Link>
        {!noNavigation && (
          <>
            {!!headerV2?.navigationLeftSide?.length && (
              <Box className={classes.leftColumn}>
                <Box className={classes.navigationLeft}>
                  {headerV2?.navigationLeftSide?.map((navGroup) => (
                    <NavigationGroup
                      key={navGroup.contentful_id}
                      groupClass={classes.navigationGroup}
                      linkClass={classes.link}
                      navigationGroup={navGroup}
                      expanded={expanded}
                      handleChange={handleClickChange}
                      handleHoverChange={setExpanded}
                    />
                  ))}
                </Box>
              </Box>
            )}
            <Box className={classes.navigationRight}>
              {headerV2?.navigationRightSide?.map((navGroup) => (
                <React.Fragment key={navGroup.contentful_id}>
                  {navGroup.__typename === 'ContentfulNavigationGroup' && (
                    <Box className={classes.navGroupContainer}>
                      {shouldReplaceWithHIW(navGroup.contentful_id) ? (
                        <HIWNavGroup />
                      ) : (
                        <NavigationGroup
                          accordionClass={classes.accordion}
                          accordionClasses={{
                            root: classes.accordion,
                            expandIconWrapper: classes.accordionSVG,
                            accordionSummaryContent:
                              classes.accordionSummaryContent,
                            accordionSummaryExpanded:
                              classes.accordionSummaryExpanded,
                            accordionDetails: classes.accordionDetails,
                            accordionDetailEntry: classes.accordionDetailEntry,
                            accEntryLink: classes.accEntryLink,
                          }}
                          linkClass={classes.link}
                          navigationGroup={navGroup}
                          expanded={expanded}
                          handleChange={handleClickChange}
                          handleHoverChange={setExpanded}
                        />
                      )}
                    </Box>
                  )}
                  {navGroup.__typename === 'ContentfulButton' &&
                    renderButton(navGroup)}
                </React.Fragment>
              ))}
            </Box>
            <Box className={classes.showOnMobile}>
              {!open && buyNowButton?.__typename === 'ContentfulButton' && (
                <NavigationButton
                  link={buyNowButton}
                  className={cx(classes.showOnMobile, classes.navButton)}
                  onClick={() => {
                    telemetry.track({ event: 'click_buy_now_header' }) // Track the "Buy Now" event
                  }}
                />
              )}
              <IconButton
                aria-label={open ? 'close' : 'menu'}
                className={classes.iconButton}
                color="inherit"
                onClick={() =>
                  setMenuState(
                    open ? MenuState.MENU_CLOSED : MenuState.MENU_OPENED
                  )
                }
                size="large"
              >
                {open ? <Close /> : <Menu />}
              </IconButton>
            </Box>
          </>
        )}
      </Box>
      {!noNavigation && (
        <>
          {showBlogNavigation && <BlogNavigation className={classes.blogNav} />}
          {open && (
            <Box className={classes.drawer}>
              {showBlogNavigation && <BlogNavigation />}
              <Box className={classes.drawerLinkContainer}>
                <Box>
                  {menuState === MenuState.MENU_OPENED &&
                    headerV2?.navigationRightSide?.map((navGroup) => (
                      <React.Fragment key={navGroup.contentful_id}>
                        {navGroup.__typename === 'ContentfulNavigationGroup' &&
                          (shouldReplaceWithHIW(navGroup.contentful_id) ? (
                            <HIWNavGroup groupClass={classes.navigationGroup} />
                          ) : (
                            <NavigationGroup
                              accordionClass={classes.accordion}
                              accordionClasses={{
                                root: classes.accordion,
                                expandIconWrapper: classes.accordionSVG,
                                accordionSummaryContent:
                                  classes.accordionSummaryContent,
                                accordionSummary: classes.accordionSummary,
                                accordionSummaryExpanded:
                                  classes.accordionSummaryExpanded,
                                accordionDetails: classes.accordionDetails,
                                accordionDetailEntry:
                                  classes.accordionDetailEntry,
                                accEntryLink: classes.accEntryLink,
                              }}
                              groupClass={classes.navigationGroup}
                              linkClass={classes.link}
                              navigationGroup={navGroup}
                              key={navGroup.contentful_id}
                              expanded={expanded}
                              handleChange={handleClickChange}
                              handleHoverChange={setExpanded}
                            />
                          ))}
                        {navGroup.__typename === 'ContentfulButton' && (
                          <Box className={classes.drawerButtonContainer}>
                            <NavigationButton
                              className={cx({
                                [classes.navigationButtonDrawer]: true,
                                [classes.langBtn]:
                                  navGroup.role === ButtonRole.LanguageSwitcher,
                              })}
                              link={navGroup}
                              onClick={() => {
                                setMenuState(MenuState.MENU_CLOSED)

                                if (
                                  navGroup.role === ButtonRole.LanguageSwitcher
                                ) {
                                  showRegions()
                                }
                              }}
                            />
                          </Box>
                        )}
                      </React.Fragment>
                    ))}
                  {menuState === MenuState.REGIONS_OPENED &&
                    regionsSelectStep !== undefined && (
                      <Box className={classes.mobileRegionsContainer}>
                        <ContentfulModalStep
                          variant={ModalVariants.LanguageSwitcher}
                          {...regionsSelectStep}
                        />
                      </Box>
                    )}
                </Box>
              </Box>
            </Box>
          )}
          {!open && showBlogNavigation && (
            <ScrollProgress className={classes.ScrollProgress} />
          )}
        </>
      )}
    </AppBar>
  )
}

// HIW - How It Works
const HIWNavGroup: React.FC<Omit<NavigationGroupProps, 'navigationGroup'>> = (
  props
) => {
  const { t } = useTranslation()
  const { flagshipProduct } = useBrand()
  const tRef = 'navigation.hiw'

  return (
    <NavigationGroup
      key="#how-it-works"
      navigationGroup={{
        title: t(`${tRef}.title`),
        text: '',
        links: [
          {
            contentful_id: '#how-it-works',
            text: t(`${tRef}.linkText`),
            action: null,
            __typename: 'ContentfulInternalLink',
            slug:
              t('slugs.howItWorks', {
                product: flagshipProduct?.toLowerCase(),
              }) + window.location.search,
            icon: null,
            codeClasses: null,
            mixpanelTracking: null,
          },
        ],
        contentful_id: '#navgroup',
        category: null,
        variant: null,
        __typename: 'ContentfulNavigationGroup',
      }}
      {...props}
    />
  )
}

const useStyles = makeStyles()((theme) => ({
  appBar: {
    backgroundColor: theme.palette.background.default,
    whiteSpace: 'pre',
    '& > *': {
      color: theme.palette.text.primary,
    },
    '& a': {
      color: theme.palette.text.primary,
      '&:hover, &:active': {
        color: theme.palette.text.primary,
      },
    },
    border: `1px solid ${theme.colors.neutral[15]}`,
  },
  appBarWihoutNavigation: {
    borderColor: 'transparent !important',
    backgroundColor: 'transparent !important',
  },
  blogNav: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  drawer: {
    display: 'none',
    borderTop: `1px solid ${theme.colors.neutral[15]}`,
    [theme.breakpoints.down('lg')]: {
      display: 'unset',
      height: 'calc(100vh - 3.59em)',
      paddingBottom: '.5em',
    },
  },
  drawerButtonContainer: {
    paddingInline: '6%',

    [theme.breakpoints.down('lg')]: {
      marginTop: '24px',
    },
  },
  drawerLinkContainer: {
    height: 'calc(100% - 3em)',
    overflow: 'scroll',
  },
  iconButton: {
    display: 'none',
    [theme.breakpoints.down('lg')]: {
      display: 'unset',
      padding: '0',
      marginLeft: '1rem',
    },
  },
  link: {
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      display: 'inline-block',
    },
    margin: '0 2.5%',
    display: 'flex',
  },
  navGroupContainer: {
    width: 'auto',
    margin: 0,
    fontSize: '1.118em',

    // To stop the flicker on hover
    '&:nth-child(2)': {
      minWidth: '5rem',
    },

    // To stop the flicker on hover
    '&:nth-child(3)': {
      padding: '0px 2px 0px 2px',
    },
  },
  navigationButtonDrawer: {
    paddingBlock: '0.75rem',

    [theme.breakpoints.down(768)]: {
      width: '100%',
    },
  },
  navigationGroup: {
    width: '83%',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      padding: '0 6% 0 6%',
      width: '100%',
      marginBlock: '3rem',

      '& > *': {
        fontSize: '1.875rem',
        fontWeight: 700,
        margin: '0',
      },
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      overflow: 'hidden',
      padding: '0 24px 0 24px!important',
      marginTop: '32px!important',
    },
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'row',
    width: 'max-content',
    margin: 0,
  },
  navigationLeft: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    margin: 'unset',
    width: '38em',

    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },
  navigationRight: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '2.5rem',
    margin: 'unset',
    maxWidth: '65%',
    fontFamily: 'tt-talent, Helvetica, Arial, sans-serif',

    [theme.breakpoints.down('lg')]: {
      justifyContent: 'flex-end',
      width: '100%',
      display: 'none',
      '& > *': {
        margin: '0 1em',
      },
    },
  },
  topBar: {
    maxHeight: '84px',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    maxWidth: '100%',
    zIndex: 100,
    padding: '1rem 2rem',
    gap: '0.5rem',

    [theme.breakpoints.down('lg')]: {
      padding: '1rem',
    },
  },
  accordion: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,

    [theme.breakpoints.down('lg')]: {
      marginTop: '24px',
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '24px!important',
    },
    '& a:focus': {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  accordionSVG: {
    fill: theme.palette.text.primary,
  },
  accordionSummary: {
    border: 'none',
    marginBottom: '0.875rem',

    '&&': {
      minHeight: 'fit-content',
    },
  },
  accordionSummaryContent: {
    [theme.breakpoints.down('lg')]: {
      '&&': {
        margin: 0,
        fontSize: '1.875rem',
        fontWeight: 700,
        padding: 0,
      },
    },
  },
  accordionSummaryExpanded: {
    fontWeight: 600,
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.875rem',
      fontWeight: 700,
      padding: '1rem 0',
    },
  },
  accordionDetails: {
    [theme.breakpoints.down('lg')]: {
      paddingBottom: 0,
    },

    [theme.breakpoints.up('lg')]: {
      borderRadius: '8px',
      marginTop: '-1rem',
      zIndex: 1,
      backgroundColor: theme.palette.background.default,
    },
  },
  accordionDetailEntry: {
    fontSize: '1.25rem',

    [theme.breakpoints.up('lg')]: {
      fontSize: '1.0625rem',
    },

    [theme.breakpoints.down('lg')]: {
      paddingBlock: '0.5rem',
    },
  },
  accEntryLink: {
    '&&&': {
      '&:hover': {
        color: theme.palette.action.active,
      },
    },
  },
  ScrollProgress: {
    backgroundColor: theme.palette.primary.dark ?? theme.palette.text.hint,
  },
  navButton: {
    width: 'auto',
    fontSize: '16px',
    paddingBlock: '0.75rem',
    lineHeight: '1.375rem',
    marginBlock: '1rem',
    borderRadius: '32px',
  },
  showOnMobile: {
    display: 'none',
    [theme.breakpoints.down('lg')]: {
      display: 'unset',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
  logoLink: {
    display: 'flex',
    maxWidth: '15rem',
    '&:hover': {
      opacity: 0.7,
    },
  },
  logo: {
    maxWidth: '14rem',
    maxHeight: '46px',

    [theme.breakpoints.up('mobile')]: {
      maxWidth: '8rem',
      maxHeight: '36px',
    },

    [theme.breakpoints.up('sm')]: {
      maxWidth: '12rem',
      maxHeight: '44px',
    },

    [theme.breakpoints.up('laptop')]: {
      maxWidth: '14rem',
      maxHeight: '46px',
    },
  },
  mobileRegionsContainer: {
    padding: '0 6% 0 6%',
    '& p': {
      display: 'flex',
      flexDirection: 'column',
    },
    '& button': {
      alignSelf: 'flex-start',
      marginRight: 0,
      marginLeft: 0,
    },
  },
  langBtn: {
    '& span > svg': {
      width: '24px',
      height: '24px',
    },
    '&:hover': {
      color: colors.blue[300],
      backgroundColor: theme.colors.neutral[5],
    },
    width: '45px !important',
    padding: 0,
    color: '#1E2532',
    fontSize: theme.typography.fontSize,
    fontFamily: theme.typography.h1.fontFamily,
  },
}))

export default withBannerAccommodator(HeaderV2)
